/**
 * Central, deduped "what does this sm_id look like" cache. `myRequests.ts` and `likedTracks.ts`
 * both used to carry their own copy of `title`/`artist`; now they store only `smId` + a
 * timestamp, and resolve the display metadata from here at render time. Artwork is never
 * stored — it's always derived from `smId` via `artworkUrl()`.
 *
 * This is a plain map, not a `useSyncExternalStore` source: nothing renders "the cache" on its
 * own, and every write here happens synchronously right before the paired list write
 * (`addMyRequest`/`toggleLike`), which IS observed. By the time a screen re-renders off one of
 * those, the metadata is already sitting here waiting to be read.
 */

const STORAGE_KEY = "touchscreen:track_cache";
const MAX_ENTRIES = 200;

export interface CachedTrack {
  title: string;
  artist: string;
}

// Entries kept oldest-first so eviction is a simple slice. Not a plain `Record<smId, CachedTrack>`
// because JS object key order reorders purely-numeric-looking keys to numeric order regardless of
// insertion time — `sm_id` isn't guaranteed non-numeric, so insertion order is tracked explicitly.
type TrackCacheEntry = [smId: string, track: CachedTrack];

function readEntries(): TrackCacheEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as TrackCacheEntry[]) : [];
  } catch {
    return [];
  }
}

function writeEntries(entries: TrackCacheEntry[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Storage full or blocked — worst case a row falls back to "Unknown track" later.
  }
}

/** Upsert. Caps total distinct tracks so a long-lived device doesn't grow this unbounded. */
export function cacheTrack(smId: string, track: CachedTrack): void {
  const entries = readEntries().filter(([existingId]) => existingId !== smId);
  entries.push([smId, track]);
  writeEntries(entries.slice(-MAX_ENTRIES));
}

export function getCachedTrack(smId: string): CachedTrack | null {
  const entries = readEntries();
  for (let i = entries.length - 1; i >= 0; i--) {
    const entry = entries[i];
    if (entry && entry[0] === smId) return entry[1];
  }
  return null;
}
